// React and Redux imports
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'

// Material-UI imports
import { Box, Stack, Typography, Paper } from '@mui/material'
import { Grid } from '@mui/material_modern'

// Custom components and context imports
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import BulkInvoicePaymentTable from '../tables/BulkInvoicePaymentTable'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'

// Utility and service imports
import { formatNumber, dispatchSnackbar } from 'utils/helpers'
import { useDeleteInvoiceBulkPaymentMutation } from 'services/invoicePayments'
import { useGetInvoicePaymentQuery } from 'services/invoicePayments'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'

const BulkInvoicePaymentContainer: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { id } = useParams()
  const [showVoidPaymentModal, setShowVoidPaymentModal] = useState(false)
  const [deleteInvoicePayment] = useDeleteInvoiceBulkPaymentMutation()
  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)
  const { defaultCurrency } = useSelector(state => (state as any).settings)

  const { data: paymentInfo, isLoading: isLoadingPaymentInfo } = useGetInvoicePaymentQuery(
    { paymentId: id },
    { refetchOnMountOrArgChange: true, skip: !id },
  )

  const { pricePrepend } = useGetPricePrepend({ selectedCurrency: defaultCurrency })

  const INVOICE_PAYMENT_ACTIONS = useMemo(
    () => [
      {
        text: 'VOID PAYMENT',
        id: 'void-invoice-payment-button',
        disabled: false,
        sx: {
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
          color: '#F44336',
          textTransform: 'uppercase',
          fontWeight: 'bold',
          padding: '6px 16px',
          '&:hover': {
            backgroundColor: 'white',
            opacity: 0.8,
          },
        },
        'aria-label': 'Void Invoice Payment Button',
        'data-testid': 'void-invoice-payment-button',
        onClick: () => setShowVoidPaymentModal(true),
      },
    ],
    [setShowVoidPaymentModal],
  )

  const isLoading = isLoadingPaymentInfo
  useGlobalLoader(isLoading)

  const handleVoidInvoicePayment = useCallback(async () => {
    await deleteInvoicePayment({
      paymentId: paymentInfo?.id,
      invoiceId: paymentInfo?.invoiceId,
      customerId: paymentInfo?.customerId,
    })
      .unwrap()
      .then(() => {
        dispatchSnackbar(dispatch, 'info', 'Invoice Payment Voided')
        navigate('/app/sales/invoice-payments')
        setShowVoidPaymentModal(false)
      })
      .catch(error => {
        const errorMessage = error?.data?.error || error?.error || error?.message || 'Error Voiding Invoice Payment'
        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
    return null
  }, [
    deleteInvoicePayment,
    dispatch,
    setShowVoidPaymentModal,
    navigate,
    paymentInfo?.id,
    paymentInfo?.customerId,
    paymentInfo?.invoiceId,
  ])

  useEffect(() => {
    if (contentContext) {
      contentContext.setContainerActions(INVOICE_PAYMENT_ACTIONS)
    }
  }, [contentContext, INVOICE_PAYMENT_ACTIONS])

  useEffect(() => {
    contentContext?.setContainerTitle(
      paymentInfo?.paymentNumber ? `Invoice Payment #${paymentInfo.paymentNumber}` : 'Invoice Payment',
    )
    tabsContext?.setContainerTabsVisibility(false)
  }, [contentContext, tabsContext, paymentInfo])

  return (
    <>
      <Box
        sx={{ padding: '20px', width: '100%', maxWidth: '800px', maxHeight: '100%', overflowY: 'auto' }}
        data-testid="payment-info-box"
      >
        <Typography variant="h6" gutterBottom data-testid="payment-info-title">
          Payment Information
        </Typography>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            padding: '16px',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.400',
            backgroundColor: 'background.section',
            boxShadow: 'none',
          }}
          data-testid="payment-info-paper"
        >
          <Grid container spacing={2} data-testid="payment-info-grid">
            <Grid item xs={6} data-testid="payment-info-grid-left">
              <Stack spacing={2}>
                <Box data-testid="consumer-name-box">
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    data-testid="consumer-name-label"
                  >
                    Consumer Name
                  </Typography>
                  <Typography variant="body1" data-testid="consumer-name-value">
                    {paymentInfo?.customerName}
                  </Typography>
                </Box>
                <Box data-testid="payment-method-box">
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    data-testid="payment-method-label"
                  >
                    Payment Method
                  </Typography>
                  <Typography variant="body1" data-testid="payment-method-value">
                    {paymentInfo?.paymentType.toUpperCase() || ''}
                  </Typography>
                </Box>
                <Box data-testid="payment-amount-box">
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    data-testid="payment-amount-label"
                  >
                    Payment Amount
                  </Typography>
                  <Typography variant="body1" data-testid="payment-amount-value">
                    {pricePrepend}
                    {formatNumber((Number(paymentInfo?.amount) || 0).toFixed(2))}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={6} data-testid="payment-info-grid-right">
              <Stack spacing={2}>
                <Box data-testid="payment-date-box">
                  <Typography variant="caption" display="block" color="text.secondary" data-testid="payment-date-label">
                    Payment Date
                  </Typography>
                  <Typography variant="body1" data-testid="payment-date-value">
                    {paymentInfo?.paymentDate}
                  </Typography>
                </Box>
                {paymentInfo?.checkNumber && (
                  <Box data-testid="check-number-box">
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      data-testid="check-number-label"
                    >
                      Check #
                    </Typography>
                    <Typography variant="body1" data-testid="check-number-value">
                      {paymentInfo.checkNumber}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Box>
      <Box sx={{ px: '20px', width: '95%', mt: 2 }} data-testid="invoices-box">
        <Typography variant="h6" sx={{ mt: 1, mb: 1 }} data-testid="invoices-title">
          Invoices
        </Typography>
        <BulkInvoicePaymentTable invoices={paymentInfo?.paymentSplits || []} data-testid="bulk-invoice-payment-table" />
        {paymentInfo?.notes && (
          <>
            <Typography variant="h6" gutterBottom sx={{ mt: 6 }} data-testid="payment-note-title">
              Payment Note
            </Typography>
            <Box data-testid="payment-note-box">
              <Typography data-testid="payment-note-value">{paymentInfo?.notes}</Typography>
            </Box>
          </>
        )}
      </Box>

      <AlertModal
        data-testid="invoice-payment-void-payment-modal"
        open={showVoidPaymentModal}
        modalType="delete"
        title=""
        question="Are you sure you want to void this payment?"
        description="Voiding this payment will return these invoices to an “Open” status."
        confirmLabel="YES, CONFIRM VOID"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowVoidPaymentModal(false)
        }}
        onSubmit={() => handleVoidInvoicePayment()}
      />
    </>
  )
}

export default BulkInvoicePaymentContainer
